import { useWindowSize } from '@website/hooks/useWindowsSize';
import { TripsPageQuery } from '@website/types/graphqlOperations';
import clsx from 'clsx';
import { useMemo } from 'react';
import Imgix from 'react-imgix';
import ReactPlayer from 'react-player';
import { getImageUrl } from '../../media/utils/imgix';
import styles from './TripImages.module.scss';
import useTripsDiscountRate from './useTripsDiscountRate';

type TripImagesProps = {
  trip: TripsPageQuery['tripByURL'];
};

export default function TripImages({ trip }: TripImagesProps) {
  const size = useWindowSize();

  const isMobile = size.width < 576;

  const featuredImage = useMemo(
    () => trip.featuredImages?.[0] ?? trip.image,
    [trip.featuredImages, trip.image],
  );

  const aspectRatio = useMemo(() => {
    // 4:1 for larger screens, 3:1 for smaller screens, 3:2 for tablets and 1:1
    // for mobile devices
    if (size.width < 576) {
      return '1:1';
    }
    if (size.width < 768) {
      return '3:2';
    }
    if (size.width < 992) {
      return '3:1';
    }
    if (size.width < 1200) {
      return '2:1';
    }
    return '4:1';
  }, [size.width]);

  const discountRate = useTripsDiscountRate(trip);

  if (trip.tripVideoURL && trip.featuredImages?.length > 1 && !isMobile) {
    return (
      <div className={styles.featuredContainer}>
        <div className={styles.featuredVideo}>
          <ReactPlayer
            height="100%"
            width="100%"
            url={trip.tripVideoURL}
            controls
            playing
            playIcon={<></>}
            light={
              trip.tripVideoURLThumbnail?.file?.key
                ? getImageUrl(trip.tripVideoURLThumbnail?.file?.key)
                : true
            }
          />
        </div>
        {trip.featuredImages.slice(0, 2).map((image) => (
          <div key={image?.file?.key} className={styles.featuredImages}>
            <Imgix
              src={getImageUrl(image?.file?.key)}
              srcSetOptions={{
                widths: [400, 600, 800],
                devicePixelRatios: [1, 2],
                maxWidth: 800,
                minWidth: 400,
              }}
              htmlAttributes={{
                style: {
                  aspectRatio: '16/9',
                },
                alt: trip.title,
              }}
              imgixParams={{
                ar: '16:9',
                auto: 'compress,format,enhance,redeye',
                crop: 'faces,edges',
                fit: 'crop',
              }}
              className="aspect-ratio aspect-ratio-16x9 w-100"
            />
          </div>
        ))}
      </div>
    );
  }

  if (trip.featuredImages?.length > 2 && !isMobile) {
    const image = trip.featuredImages[0];

    return (
      <>
        <div className={styles.featuredContainer}>
          <div className={styles.featuredVideo}>
            <Imgix
              key={image?.file?.key}
              src={getImageUrl(image?.file?.key)}
              srcSetOptions={{
                widths: [400, 600, 800, 1200],
                devicePixelRatios: [1, 2],
                maxWidth: 1600,
                minWidth: 400,
              }}
              htmlAttributes={{
                style: {
                  aspectRatio: '16/9',
                },
                alt: trip.title,
              }}
              imgixParams={{
                ar: '16:9',
                auto: 'compress,format,enhance,redeye',
                crop: 'faces',
                fit: 'crop',
              }}
              className="w-100 h-100"
            />
          </div>
          {trip.featuredImages.slice(1, 3).map((img) => (
            <div key={img?.file?.key} className={styles.featuredImages}>
              <Imgix
                src={getImageUrl(img?.file?.key)}
                srcSetOptions={{
                  widths: [400, 600, 800],
                  devicePixelRatios: [1, 2],
                  maxWidth: 1600,
                  minWidth: 400,
                }}
                htmlAttributes={{
                  style: {
                    aspectRatio: '16/9',
                  },
                  alt: trip.title,
                }}
                imgixParams={{
                  ar: '16:9',
                  auto: 'compress,format,enhance,redeye',
                  crop: 'faces,entropy',
                  fit: 'crop',
                }}
                className={clsx('aspect-ratio aspect-ratio-16x9 w-100')}
              />
            </div>
          ))}
        </div>
      </>
    );
  }

  return (
    <div className={styles.fallbackSection}>
      {(trip.overlayText || trip.minimumPrice?.priceBeforeDiscount) && (
        <div className={`${styles.ribbon}`}>
          <span>
            {discountRate ? `ON SALE: Save ${discountRate}%` : trip.overlayText}
          </span>
        </div>
      )}
      {/* Show 1:1 aspect ratio for mobile (bootstrap xs) and 4:1 for larger screens (sm+)  */}
      <div className={styles.mobileFeaturedImages}>
        <Imgix
          imgixParams={{
            auto: 'compress,format,enhance,redeye',
            crop: 'faces',
            fit: 'crop',
            ar: '1:1',
          }}
          src={`${getImageUrl(featuredImage?.file?.key)}`}
          sizes="100vw"
          className="w-100 h-100"
        />
      </div>
      <div className={styles.desktopFeaturedImages}>
        <Imgix
          imgixParams={{
            auto: 'compress,format,enhance,redeye',
            crop: 'faces',
            fit: 'crop',
            ar: aspectRatio,
          }}
          src={`${getImageUrl(featuredImage?.file?.key)}`}
          sizes="100vw"
          className="w-100 h-100"
          htmlAttributes={{
            alt: trip.title,
          }}
        />
      </div>
    </div>
  );
}
